import { useSettingStore } from '../../store/settingStore';
import {
  getSetting as fetchSettingApi,
  updateGuideVisit,
  updateSeo,
  updateSetting,
  updateNotification,
} from '../../api/settingApi';
import { SettingData } from '../../dto/EntityRepository';
import { SettingResponse } from '../../dto/ResDtoRepository';
import { UpdateSettingReq, UpdateGuideVisitReq, UpdateNotificationReq, UpdateSeoReq } from '../../dto/ReqDtoRepository';
import { useTempAlertStore } from '../../store/portal/tempAlertStore';
import { useApiWrapper } from './apiWrapper';
import { usePageOwnerIdentityStore } from '../../store/pageOwnerIdentityStore';
import { runCurrentArchiveRequest } from '../../util/currentArchiveRequest';
import { useModalStore } from '../../store/portal/modalStore';
import { resolveNavLayout } from '../../util/navLayout';

interface UseSettingResult {
  setting: SettingData | null;
  fetchSetting: (aui: string) => Promise<SettingData | undefined>;
  updateSetting: (data: UpdateSettingReq) => Promise<void>;
  updateSeo: (data: UpdateSeoReq) => Promise<void>;
  updateGuideVisit: (data: UpdateGuideVisitReq) => Promise<void>;
  updateNotification: (data: UpdateNotificationReq) => Promise<void>;
  clearSetting: () => void;
}

export const useSetting = (): UseSettingResult => {
  const pageOwner = usePageOwnerIdentityStore(s => s.pageOwner);
  const { setting, setSetting, clearSetting } = useSettingStore();
  const { setUpdatedTempAlert } = useTempAlertStore();
  const { clearModal } = useModalStore();
  const withApiHandler = useApiWrapper();

  const handleFetchSettingSuccess = (response: SettingResponse): SettingData => {
    setSetting(response.data);
    return response.data;
  };

  const handleUpdateSettingSuccess = (response: SettingResponse, sent?: UpdateSettingReq) => {
    const settingData = response.data;
    setSetting({
      ...settingData,
      navLayout: resolveNavLayout(settingData.navLayout ?? sent?.navLayout),
    });
    setUpdatedTempAlert();
  };

  const handleUpdateSeoSuccess = (response: SettingResponse) => {
    handleUpdateSettingSuccess(response);
    clearModal();
  };

  const handleUpdateGuideVisitSuccess = (response: SettingResponse) => {
    const settingData = response.data;
    setSetting(settingData);

    // if (user) {
    //   setUser({ ...user, landingGuide: settingData.guideVisit.landingGuide });
    // }
  };

  const handleUpdateNotificationSuccess = (response: SettingResponse) => {
    const settingData = response.data;
    setSetting(settingData);
    setUpdatedTempAlert();
  };

  const handleSettingRequest = async (
    action: 'update' | 'updateSeo' | 'updateGuideVisit' | 'updateNotification',
    data?: UpdateSettingReq | UpdateSeoReq | UpdateGuideVisitReq | UpdateNotificationReq
  ) => {
    if (!pageOwner) return;
    const { aui } = pageOwner;
    const apiFunction = async () => {
      switch (action) {
        case 'updateNotification':
          return runCurrentArchiveRequest(
            aui,
            () => updateNotification(aui, data as UpdateNotificationReq),
            handleUpdateNotificationSuccess,
            { matchPath: false },
          );
        case 'updateSeo':
          return runCurrentArchiveRequest(
            aui,
            () => updateSeo(aui, data as UpdateSeoReq),
            handleUpdateSeoSuccess,
            { matchPath: false },
          );
        case 'update':
          return runCurrentArchiveRequest(
            aui,
            () => updateSetting(aui, data as UpdateSettingReq),
            (response) => handleUpdateSettingSuccess(response, data as UpdateSettingReq),
            { matchPath: false },
          );
        default:
        case 'updateGuideVisit':
          return runCurrentArchiveRequest(
            aui,
            () => updateGuideVisit(aui, data as UpdateGuideVisitReq),
            handleUpdateGuideVisitSuccess,
            { matchPath: false },
          );
      }
    };

    await withApiHandler(apiFunction, [aui, action, data]);
  };


  const handleFetchSettingRequest = async (
    aui: string,
  ): Promise<SettingData | undefined> => {
    const apiFunction = async () => {
      return runCurrentArchiveRequest(
        aui,
        () => fetchSettingApi(aui),
        handleFetchSettingSuccess,
        { matchPath: false },
      );
    };
    return await withApiHandler(apiFunction, [aui, 'get', null]);
  };

  const fetchSettingHandler = (aui: string) => handleFetchSettingRequest(aui);
  const updateSettingHandler = (data: UpdateSettingReq) => handleSettingRequest('update', data);
  const updateSeoHandler = (data: UpdateSeoReq) => handleSettingRequest('updateSeo', data);
  const updateGuideVisitHandler = (data: UpdateGuideVisitReq) => handleSettingRequest('updateGuideVisit', data);
  const updateNotificationHandler = (data: UpdateNotificationReq) => handleSettingRequest('updateNotification', data);

  return {
    setting,
    fetchSetting: fetchSettingHandler,
    updateSetting: updateSettingHandler,
    updateSeo: updateSeoHandler,
    updateGuideVisit: updateGuideVisitHandler,
    updateNotification: updateNotificationHandler,
    clearSetting
  };
}
